import { Controller } from "@hotwired/stimulus"

const SESSION_PATTERN = /TargetLogonId\s*==.*OR\s*SubjectLogonId\s*==.*OR\s*LinkedLogonId\s*==/i

// Renders session-correlated results as a host-grouped vertical timeline.
// Activated when the current query matches a session correlation pattern.
export default class extends Controller {
  static targets = ["tablePane", "timelinePane", "toggleBtn"]

  #active = false
  #boundInput = null

  connect() {
    this.#boundInput = () => this.#checkQuery()
    document.addEventListener("input", this.#boundInput)
    this.#checkQuery()
  }

  disconnect() {
    document.removeEventListener("input", this.#boundInput)
  }

  toggle() {
    if (!this.hasToggleBtnTarget || this.toggleBtnTarget.disabled) return
    this.#active = !this.#active
    this.#active ? this.#buildTimeline() : this.#showTable()
    this.toggleBtnTarget.classList.toggle("active", this.#active)
  }

  // Called by turbo:frame-render after new results load — reset to table view
  reset() {
    if (this.#active) {
      this.#active = false
      this.#showTable()
      if (this.hasToggleBtnTarget) this.toggleBtnTarget.classList.remove("active")
    }
    this.#checkQuery()
  }

  #checkQuery() {
    const query = document.querySelector("textarea[name='query']")?.value || ""
    const isSession = SESSION_PATTERN.test(query)
    if (this.hasToggleBtnTarget) {
      this.toggleBtnTarget.disabled = !isSession
      this.toggleBtnTarget.title = isSession
        ? "Toggle session timeline view"
        : "Session timeline — run a session correlation first (right-click a LogonId)"
      this.toggleBtnTarget.classList.toggle("siem-timeline-btn--ready", isSession)
    }
    if (!isSession && this.#active) {
      this.#active = false
      this.#showTable()
    }
  }

  #buildTimeline() {
    if (!this.hasTablePaneTarget || !this.hasTimelinePaneTarget) return

    const headers = [...this.tablePaneTarget.querySelectorAll("thead th")]
      .map((th, i) => ({ text: th.textContent.trim().toLowerCase(), i }))

    const col = name => headers.find(h => h.text === name.toLowerCase())?.i ?? -1
    const tsIdx  = col("timestamp")
    const cmpIdx = col("computer")
    const eidIdx = col("eventid")
    const unIdx  = Math.max(col("targetusername"), col("subjectusername"))

    const rows = [...this.tablePaneTarget.querySelectorAll("tbody tr[id]")]
    const events = rows.map(row => {
      const cells = [...row.querySelectorAll("td")]
      return {
        id:        row.id,
        timestamp: cells[tsIdx]?.textContent?.trim() || "",
        computer:  cells[cmpIdx]?.textContent?.trim() || "Unknown",
        eventId:   cells[eidIdx]?.textContent?.trim() || "",
        user:      unIdx >= 0 ? cells[unIdx]?.textContent?.trim() : "",
      }
    }).filter(e => e.timestamp)

    if (!events.length) {
      this.timelinePaneTarget.innerHTML = '<div class="siem-timeline-empty"><i class="fa fa-info-circle"></i> No results to render as timeline.</div>'
      this.#swap()
      return
    }

    const byHost = {}
    events.forEach(e => {
      (byHost[e.computer] ??= []).push(e)
    })

    this.timelinePaneTarget.innerHTML = `
      <div class="siem-session-timeline">
        <div class="siem-timeline-legend">
          <span class="siem-tl-dot siem-tl-dot--crit"></span> Failed/suspicious
          <span class="siem-tl-dot siem-tl-dot--high" style="margin-left:10px"></span> Logon/auth
          <span class="siem-tl-dot siem-tl-dot--med"  style="margin-left:10px"></span> Process/account
          <span class="siem-tl-dot"                   style="margin-left:10px"></span> Other
        </div>
        ${Object.entries(byHost).map(([host, evts]) => `
          <div class="siem-timeline-host">
            <div class="siem-timeline-host-label">
              <i class="fa fa-desktop"></i>
              ${this.#esc(host)}
              <span class="siem-timeline-event-count">${evts.length} event${evts.length !== 1 ? "s" : ""}</span>
            </div>
            <div class="siem-timeline-track">
              ${evts.map(e => `
                <div class="siem-timeline-event ${this.#severityClass(e.eventId)}"
                     data-row-id="${this.#esc(e.id)}"
                     role="button" tabindex="0"
                     title="${this.#esc(e.timestamp)}">
                  <span class="siem-tl-dot ${this.#severityClass(e.eventId).replace("siem-timeline-event--", "siem-tl-dot--")}"></span>
                  <span class="siem-tl-time">${this.#fmtTime(e.timestamp)}</span>
                  <span class="siem-tl-eid">${this.#esc(e.eventId)}</span>
                  ${e.user && e.user !== "-" ? `<span class="siem-tl-user">${this.#esc(e.user)}</span>` : ""}
                </div>
              `).join("")}
            </div>
          </div>
        `).join("")}
      </div>
    `

    // Clicking a timeline event scrolls to + highlights the original row
    this.timelinePaneTarget.querySelectorAll(".siem-timeline-event[data-row-id]").forEach(el => {
      el.addEventListener("click", () => {
        const row = document.getElementById(el.dataset.rowId)
        if (!row) return
        this.#showTable()
        this.#active = false
        this.toggleBtnTarget?.classList.remove("active")
        row.scrollIntoView({ behavior: "smooth", block: "center" })
        row.classList.add("siem-tl-highlight")
        setTimeout(() => row.classList.remove("siem-tl-highlight"), 2000)
      })
    })

    this.#swap()
  }

  #showTable() {
    this.tablePaneTarget?.classList.remove("hidden")
    this.timelinePaneTarget?.classList.add("hidden")
  }

  #swap() {
    this.tablePaneTarget?.classList.add("hidden")
    this.timelinePaneTarget?.classList.remove("hidden")
  }

  #severityClass(eid) {
    if (["4625", "4648", "4771", "4776"].includes(eid)) return "siem-timeline-event--crit"
    if (["4624", "4768", "4769", "4634"].includes(eid)) return "siem-timeline-event--high"
    if (["4688", "4720", "4728", "4732"].includes(eid)) return "siem-timeline-event--med"
    return ""
  }

  #fmtTime(ts) {
    try { return new Date(ts).toLocaleTimeString("en-US", { hour12: false }) }
    catch { return ts.slice(11, 19) || ts }
  }

  #esc(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
